import { browser } from 'protractor';
import { GenericType } from '../../../../model/base/edl-generic-type.model';
import { ToastrService } from 'ngx-toastr';
import { EdlComment } from '../../../../model/comment.model';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CommentLookUpTypes } from 'src/app/model/enum/comment-lookup-types.enum';
import { CustomValidators } from 'src/app/validators/custom-validators';
import { CommonValidators, FileDownloadHandler, HttpErrorMessageHelper } from '@edl-development/edl-common';
import { ObservationSummaryDTO } from 'src/app/model/observation/observation-summary-dto.model';
import { CommentState } from 'src/app/model/enum/comment-state.enum';
import { ObservationCommentService } from '../../services/observation-comment/observation-comment.service';
import { RequestQueue } from 'src/app/utilities/request-queue/request-queue.util';
import { ModalService } from 'src/app/modals/modal.service';
import { FileAttachment } from 'src/app/model/attachment/file-attachment.model';
import { FileService } from 'src/app/services/file.service';

@Component({
    selector: 'app-observation-comment-form',
    templateUrl: './observation-comment-form.component.html',
    styleUrls: ['./observation-comment-form.component.scss']
})

export class ObservationCommentFormComponent implements OnInit, OnDestroy {
    @Input() observationSummary?: ObservationSummaryDTO;
    @Input() comment?: EdlComment;
    @Input() observationType?: GenericType;
    @Input() observationId?: string;
    @Input() defaultLookupValue?: string;
    @Input() hideLookup?: boolean = true;
    @Input() attachmentLabel: string = "";
    @Input() commentFiles?: FileAttachment[] = [];
    @Output() commentChange = new EventEmitter<EdlComment>()
    @Output() onClose = new EventEmitter<any>();

    form!: FormGroup;
    lookupTypes: Array<any> = [];
    lookupLabel: string = "";
    attachments: Array<any> = [];

    private saveQueue: RequestQueue<EdlComment | null | undefined>;

    public tinyInitOptions: Object = {
        height: 275,
        branding: false,
        menubar: false,
        plugins: [
            'autolink lists link charmap'
        ],
        toolbar: 'insert | undo redo | link | bold italic underline strikethrough | bullist numlist | removeformat ',
        mobile: {
            theme: 'mobile'
        },
        statusbar: false,
        default_link_target: '_blank',
        link_assume_external_targets: 'https',
        contextmenu: false,
        browser_spellcheck: true
    };


    public tinyKey: string = environment.FKEY;

    CommentState = CommentState;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private observationCommentService: ObservationCommentService,
        private toastr: ToastrService,
        private modalService:ModalService,
        private fileService:FileService ,
        private fileHandler: FileDownloadHandler,
    ) {
        this.saveQueue = new RequestQueue<EdlComment | null | undefined>();
    }

    ngOnInit(): void {
        let comment = this.comment != undefined ? this.comment.comment : "";

        this.lookupTypes = CommentLookUpTypes.getOptions(this.observationType);
        this.lookupLabel = CommentLookUpTypes.getLabel(this.observationType);

        let commentLookupValue = this.defaultLookupValue ?? "0";
        this.attachments = this.comment != undefined ? this.comment.attachments : [];

        this.form = this.formBuilder.group({
            Comment: [comment, [Validators.required, CommonValidators.emptyString]],
            LookupType: [commentLookupValue, this.hideLookup ? []:[Validators.required, CustomValidators.OptionListValidator(this.lookupTypes.map(x => x.Value))]]
        });

        if (this.comment?.historic) { this.form.disable(); };
    }

    ngOnDestroy(): void {
      try{
        this.saveQueue.complete();
      } catch(e) {}

    }

    public onSubmit = (form: FormGroup, published: boolean = false) => {
        var isCommentNull = !this.comment;
        this.submit(form, published, !this.comment).then((result: EdlComment | null | undefined) => {
            if (result) {
                this.toastr.success(`Comment ${ published ? 'published' : 'saved'} successfully.`);
                this.onClose.emit();
            }
        });
    }

    public autosave = (form: FormGroup) => {
        if (this.doesFormNeedSaved(form)) {
            this.submit(form, false, !this.comment);
        }
    }

    public doesFormNeedSaved(form: FormGroup) {
        return form.valid && form.dirty && form?.get('Comment')?.value !== this.comment?.comment;
    }

    private submit = ({ value, valid}: { value: { Comment: string, LookupType: string }, valid: boolean }, published: boolean = false, shouldReload: boolean): Promise<EdlComment | null | undefined> => {
        if (valid) {
            const action = this.generateSaveRequest(value.Comment, value.LookupType, published, shouldReload);
            return this.saveQueue.generateAndEnqueRequest(action);
        } else {
            this.form.markAllAsTouched();
            this.toastr.error("Form Invalid.");

            return Promise.resolve(null);
        };

    }

    private generateSaveRequest = (Comment: string, LookupType: string, published: boolean = false, shouldReload: boolean = false) => () => {
        const comment = this.generateComment(Comment, LookupType, published);

        return this.observationCommentService.saveComment(comment, shouldReload)
            .then((result: EdlComment | null | undefined) => {
                if (result) {
                    this.comment = result;
                    this.commentChange.emit(this.comment);
                    if(published)this.observationCommentService.onAttachmentChange(this.comment!);
                }

                return result;
            })
            .catch((error) => {
                this.toastr.error(HttpErrorMessageHelper.getErrorMessage(error));
                this.form?.markAsPristine();

                throw error;
            });
        }



    private generateComment = (Comment: string, LookupType: string, published: boolean = false) => {
        let c: EdlComment;
        if (this.comment == null) {
            c = {} as EdlComment;
            c.comment = Comment;
            c.commentState = published ? CommentState.PUBLISHED : CommentState.DRAFT;
            c.relationID = this.observationId as string;
            c.evalType = this.observationType?.Type ?? -1;
            c.lookupValue = LookupType;
            c.commenter = "";
            c.created = new Date();
            c.lastUpdated = new Date();
            c.posted = null;
            c.postedTime = null;
            c.userId = "";
            c.commentID = "";
            c.attachments = [];
        } else {
            c = this.comment;
            c.comment = Comment;
            c.lookupValue = LookupType;
            if (published && c.commentState == CommentState.DRAFT) c.commentState = CommentState.PUBLISHED
        }

        return c;
    }

    viewAttachement(attachment:FileAttachment){
      this.fileService
        .getFileResponse(attachment.fileID)
        .subscribe((fileResponse : Blob) => {
            this.fileHandler.saveFile(fileResponse, attachment.fileName);
        });
    }

    onAttachComment() {
      if(!this.comment){
        if(this.form.get("Comment")?.value == "") this.form.get("Comment")?.setValue("Attaching file");
        this.submit(this.form, false, false).then((result: EdlComment | null | undefined) => {
          this.comment = result!;
          this.modalService.openFileAttachmentsModal(false, this.commentFiles!, this.observationSummary!,this.attachmentLabel, result! ).then((fileResult:FileAttachment[])=>{
            this.comment!.attachments = this.attachments = fileResult;
            this.observationCommentService.onAttachmentChange(this.comment!);
            this.observationCommentService.reloadComments();
          }).catch();
        });
      } else {
        this.modalService.openFileAttachmentsModal(false, this.commentFiles!, this.observationSummary!, this.attachmentLabel, this.comment!).then((result:FileAttachment[])=>{
          this.comment!.attachments = this.attachments = result;
          this.observationCommentService.onAttachmentChange(this.comment!);
          this.observationCommentService.reloadComments();
        }).catch();
      }

    }


    deleteAttachement(attachment: FileAttachment){
      this.modalService.confirm('Confirmation', 'Are you sure you want to delete this attachment?')
      .then(() => {
      this.fileService
        .deleteFile(attachment)
        .subscribe((resp:any) => {
          this.toastr.success("Attachment removed.");

          const index = this.comment?.attachments.indexOf(attachment, 0);
          if (index! > -1) {
            this.comment?.attachments.splice(index!, 1);
          }

          this.observationCommentService.onAttachmentChange(this.comment!);
        });
      });
    }

    isReadOnly(): boolean {
        return this.comment?.historic ?? false;
    }

    isFormValid(): boolean {
        return this.form.valid;
    }

    onCancelClicked() {
        this.onClose.emit();
    }
}
